import { FEED, MANAGE_PROFILE, MANAGE_FACILITY, MANAGE_INDUSTRY } from "../constants/RoutesUrl";

/**
 * Paths that should skip verification status refetch on route change
 * to avoid duplicate API calls and redirect loops during onboarding/waiting.
 */
export const ONBOARDING_SKIP_PATHS = [
  "/onboarding-doctor",
  "/onboarding-facility",
  "/onboarding-industry",
  "/waiting",
];

/**
 * Pending/status screens that should poll verification status.
 */
export const PENDING_SCREENS = [
  "/waiting",
  "/again-verification",
  "/account-reject",
];

/**
 * Allowed app routes for fully verified and completed users.
 */
export const ALLOWED_PROTECTED_PATHS = [
  "/profile-", "/profile/", "/chat", "/setting", "/notifications",
  "/courses", "/course-detail", "/course-details", "/course-chapter-details",
  "/chapter-quiz", "/course-community", "/course-community-list",
  "/events", "/event-details", "/search", "/post/", "/webinar/", "/webinar-details",
  "/session-details", "/clinical-sessions-details",
  "/clinical/", "/clinical-", "/poll/", "/onboarding", "/feed",
  "/again-verification", "/account-reject", "/jobs", "/job-detail", "/my-reports",
  "/live-webinars", "/add-clinical", "/profile-job", "/profile-events",
  "/profile-posts", "/group-setting", "/contests", "/contest-list",
  "/contest-detail", "/new-contest", "/clinical-sessions", "/course/",
  "/event/", "/courses/all", "/courses/filter", "/events/all",
  "/paypal-payment", "/paypal-callback", "/manage-content",
  "/new-course-section", "/new-course-media", "/keratopigmentation",
  "/manage-doctor-profile", "/manage-facility-profile", "/manage-industry-profile",
  "/new-login", "/new-signup", "/upload-content", "/edit_profile",
  "/forgot-password", "/change-password", "/otp-verification", "/wallet",
  "/search/doctors", "/search/posts", "/search/webinars",
  "/search/clinicals", "/search/courses", "/search/events",
  "/stripe-payment", "/mentorship", "/agenda",
];

/**
 * Checks whether a user has completed the onboarding profile for their role.
 * @param {object} user - User data object
 * @returns {boolean} True if profile is completed
 */
export const hasCompletedProfile = (user) => {
  if (!user) return false;
  const userType = (user.user_type || user.type || "").toLowerCase();
  const docProfile = user.doctor_profile;
  const facProfile = user.facility_profile;
  const indProfile = user.industry_profile;

  // Check explicit profile completion flags
  if (
    user.is_profile_completed === 1 ||
    user.is_profile_completed === true ||
    user.is_profile_completed === "1" ||
    user.profile_completed === 1 ||
    user.profile_completed === true ||
    user.profile_completed === "1" ||
    user.is_completed === 1 ||
    user.is_completed === true ||
    user.is_completed === "1"
  ) {
    return true;
  }

  // Check localStorage explicit flags
  if (userType === "doctor" || !userType) {
    if (
      localStorage.getItem("specializationCompleted") === "true" ||
      localStorage.getItem("doctorProfileCompleted") === "true"
    ) {
      return true;
    }
  }
  if (userType === "facility" || userType === "hospital") {
    if (localStorage.getItem("facilityProfileCompleted") === "true") {
      return true;
    }
  }
  if (userType === "industry" || userType === "company") {
    if (localStorage.getItem("industryProfileCompleted") === "true") {
      return true;
    }
  }

  // Check Doctor-specific fields
  if (userType === "doctor") {
    if (user.specialization_completed === 1 || user.specialization_completed === true || user.specialization_completed === "1") return true;
    if (docProfile === true) return true;
    if (docProfile && typeof docProfile === "object" && Object.keys(docProfile).length > 0) return true;
    if (user.practice_type || user.experience || user.hospital || user.hospital_name || (Array.isArray(user.specializations) && user.specializations.length > 0)) return true;
    return false;
  }

  // Check Facility-specific fields
  if (userType === "facility" || userType === "hospital") {
    if (facProfile === true) return true;
    if (facProfile && typeof facProfile === "object" && Object.keys(facProfile).length > 0) return true;
    if (
      user.contact_name ||
      user.staff_no ||
      user.facility_type ||
      user.center_type ||
      user.contact_email ||
      user.contact_phone ||
      user.contact_number ||
      user.contact_role ||
      (Array.isArray(user.specializations) && user.specializations.length > 0) ||
      (user.type && user.type !== "facility" && user.type !== "hospital")
    ) {
      return true;
    }
    return false;
  }

  // Check Industry-specific fields
  if (userType === "industry" || userType === "company") {
    if (indProfile === true) return true;
    if (indProfile && typeof indProfile === "object" && Object.keys(indProfile).length > 0) return true;
    if (
      user.contact_name ||
      user.staff_no ||
      user.company_size ||
      user.industry_type ||
      user.category ||
      user.bio ||
      user.contact_email ||
      user.contact_phone ||
      user.contact_number ||
      user.contact_role ||
      (user.type && user.type !== "industry" && user.type !== "company")
    ) {
      return true;
    }
    return false;
  }

  // General fallback for any profile object present
  if (
    (docProfile && (docProfile === true || (typeof docProfile === "object" && Object.keys(docProfile).length > 0))) ||
    (facProfile && (facProfile === true || (typeof facProfile === "object" && Object.keys(facProfile).length > 0))) ||
    (indProfile && (indProfile === true || (typeof indProfile === "object" && Object.keys(indProfile).length > 0)))
  ) {
    return true;
  }

  return true;
};

/**
 * Returns the corresponding onboarding route for a given user type.
 * @param {string} userType
 * @returns {string}
 */
export const getOnboardingRoute = (userType) => {
  const type = (userType || "").toLowerCase();
  if (type === "facility" || type === "hospital") return MANAGE_FACILITY || "/onboarding-facility";
  if (type === "industry" || type === "company") return MANAGE_INDUSTRY || "/onboarding-industry";
  return MANAGE_PROFILE || "/onboarding-doctor";
};

/**
 * Centralized target route resolver based on user role, verification status, and profile completeness.
 * @param {object} user - User data object with verified, user_type, doctor_profile, facility_profile, industry_profile
 * @param {string|null} currentPath - Current route pathname (optional)
 * @returns {string|null} Target route path, or null if current path is already correct/allowed
 */
export const getUserTargetRoute = (user, currentPath = null) => {
  if (!user) return null;

  const verified = user.verified;
  const userType = (user.user_type || user.type || "").toLowerCase();
  const profileDone = hasCompletedProfile(user);
  const feedRoute = FEED || "/feed";
  const normPath = (currentPath || "").toLowerCase();

  const onboardingScreens = [
    "/onboarding-doctor",
    "/onboarding-facility",
    "/onboarding-industry",
  ];

  // 1. Account rejected (verified === 2)
  if (verified === 2) {
    if (userType === "facility" || userType === "industry") {
      return normPath === "/account-reject" ? null : "/account-reject";
    }
    if (user.verification_rejection_reason) {
      sessionStorage.setItem("verificationRejectionReason", user.verification_rejection_reason);
    }
    return normPath === "/again-verification" ? null : "/again-verification";
  }

  // 2. Pending verification (verified === 0)
  if (verified === 0) {
    if (userType === "doctor") {
      return normPath === "/waiting" ? null : "/waiting";
    }
    if (userType === "facility" || userType === "hospital") {
      if (!profileDone) {
        return normPath === "/onboarding-facility" ? null : "/onboarding-facility";
      }
      return normPath === "/waiting" ? null : "/waiting";
    }
    if (userType === "industry" || userType === "company") {
      if (!profileDone) {
        return normPath === "/onboarding-industry" ? null : "/onboarding-industry";
      }
      return normPath === "/waiting" ? null : "/waiting";
    }
    return normPath === "/waiting" ? null : "/waiting";
  }

  // 3. Incomplete profile for verified user (or user missing profile completion)
  if (!profileDone) {
    const onboardingRoute = getOnboardingRoute(userType);
    return normPath === onboardingRoute.toLowerCase() ? null : onboardingRoute;
  }

  // 4. Completed profile on onboarding screens -> redirect away
  if (normPath && onboardingScreens.includes(normPath)) {
    if (verified === 0) return "/waiting";
    if (verified === 2) {
      return (userType === "facility" || userType === "industry") ? "/account-reject" : "/again-verification";
    }
    return feedRoute;
  }

  // 5. Completed & Verified user on pending screens -> redirect to feed
  const restrictedScreens = [
    "/waiting",
    "/again-verification",
    "/account-reject",
  ];

  if (normPath && restrictedScreens.includes(normPath)) {
    return feedRoute;
  }

  if (normPath) {
    const isAllowed = ALLOWED_PROTECTED_PATHS.some((p) => normPath.startsWith(p.toLowerCase()) || normPath === p.toLowerCase());
    if (!isAllowed) return feedRoute;
  }

  return currentPath ? null : feedRoute;
};

/**
 * Resolves target route from stored localStorage credentials (SplashData / SignupData).
 * @param {string|null} currentPath - Current route pathname (optional)
 * @returns {string|null} Target route path
 */
export const getStoredUserTargetRoute = (currentPath = null) => {
  try {
    const splashSaved = JSON.parse(localStorage.getItem("SplashData") || "{}");
    const signupSaved = JSON.parse(localStorage.getItem("SignupData") || "{}");
    const user = splashSaved?.user || signupSaved?.data || {};

    if (!user?.id && !signupSaved?.data?.id && !signupSaved?.data?.token) {
      return null;
    }

    const mergedUser = {
      ...signupSaved?.data,
      ...splashSaved?.user,
    };

    return getUserTargetRoute(mergedUser, currentPath);
  } catch {
    return FEED || "/feed";
  }
};

/**
 * Updates localStorage and sessionStorage (SignupData and SplashData) with updated profile data.
 * @param {object} updatedData - Partial or full user profile updates
 */
export const updateStoredUserProfile = (updatedData = {}) => {
  try {
    const signupSaved = JSON.parse(localStorage.getItem("SignupData") || "{}");
    const splashSaved = JSON.parse(localStorage.getItem("SplashData") || "{}");

    const updatedSignup = {
      ...signupSaved,
      data: {
        ...(signupSaved.data || {}),
        ...updatedData,
      },
    };
    localStorage.setItem("SignupData", JSON.stringify(updatedSignup));
    sessionStorage.setItem("SignupData", JSON.stringify(updatedSignup));

    const updatedSplash = {
      ...splashSaved,
      user: {
        ...(splashSaved.user || {}),
        ...updatedData,
      },
    };
    localStorage.setItem("SplashData", JSON.stringify(updatedSplash));
  } catch (e) {
    console.warn("Storage profile sync failed:", e);
  }
};


